/**
 * ExpensesPage - Main expenses management page
 * BP-04-04: Expenses Frontend
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  PieChart,
  Calendar,
  RefreshCw,
  FileText,
  X,
  ChevronRight,
} from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import {
  useExpenses,
  useExpenseSummary,
  useExpensesByCategory,
  useDeleteExpense,
} from '../hooks/useExpenses';
import {
  ExpenseCard,
  ExpenseFilters,
  ExpenseTable,
  ExpenseCategoryChart,
  ExpenseTrendChart,
  ExpenseExportModal,
  ExpenseImportModal,
} from '../components';
import { EXPENSE_CATEGORIES, type ExpenseCategory, type Expense } from '../types';

export default function ExpensesPage() {
  const navigate = useNavigate();
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filters, setFilters] = useState<{
    category?: ExpenseCategory;
    startDate?: string;
    endDate?: string;
  }>({});

  // Get current month range
  const currentMonth = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, []);

  // Queries
  const {
    data: expensesData,
    isLoading: isLoadingExpenses,
    error: expensesError,
    refetch: refetchExpenses,
  } = useExpenses(familyId || '', {
    ...filters,
    search: searchQuery || undefined,
  });

  const { data: summary, isLoading: isLoadingSummary } = useExpenseSummary(
    familyId || '',
    currentMonth
  );

  const { data: categoryData } = useExpensesByCategory(familyId || '', currentMonth);

  // Mutations
  const deleteExpense = useDeleteExpense();

  // Computed values
  const expenses = expensesData?.items ?? [];

  // Handlers
  const handleExpenseClick = (expense: Expense) => {
    navigate(`/family/expenses/${expense.id}`);
  };

  const handleDeleteExpense = async (expense: Expense) => {
    if (window.confirm(`Delete "${expense.description}"? This action cannot be undone.`)) {
      await deleteExpense.mutateAsync(expense.id);
    }
  };

  const handleEditExpense = (expense: Expense) => {
    navigate(`/family/expenses/${expense.id}/edit`);
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  // Loading state
  if (isFamilyLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  // No family state
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy">Expenses</h1>
          <p className="text-warm-gray mt-1">
            Track and manage your family's care-related expenses
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/family/expenses/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </Link>
          <Link
            to="/family/expenses/reports"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-warm-gray/20 text-navy rounded-xl hover:bg-cream transition-colors"
          >
            <FileText className="w-4 h-4" />
            Reports
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">
                {isLoadingSummary ? '...' : formatCurrency(summary?.totalExpenses ?? 0)}
              </p>
              <p className="text-sm text-warm-gray">This Month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-terracotta/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-terracotta" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">
                {isLoadingSummary ? '...' : formatCurrency(summary?.totalOutOfPocket ?? 0)}
              </p>
              <p className="text-sm text-warm-gray">Out of Pocket</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">
                {isLoadingSummary ? '...' : formatCurrency(summary?.totalInsuranceCovered ?? 0)}
              </p>
              <p className="text-sm text-warm-gray">Insurance Covered</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <PieChart className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">
                {isLoadingSummary ? '...' : formatCurrency(summary?.totalTaxDeductible ?? 0)}
              </p>
              <p className="text-sm text-warm-gray">Tax Deductible</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to="/family/expenses/budgets"
          className="bg-white rounded-xl p-4 border border-warm-gray/10 hover:border-sage/30 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cream rounded-lg flex items-center justify-center group-hover:bg-sage/10 transition-colors">
                <Calendar className="w-5 h-5 text-navy" />
              </div>
              <span className="font-medium text-navy">Budgets</span>
            </div>
            <ChevronRight className="w-4 h-4 text-warm-gray group-hover:text-sage transition-colors" />
          </div>
        </Link>

        <button
          onClick={() => setShowImportModal(true)}
          className="bg-white rounded-xl p-4 border border-warm-gray/10 hover:border-sage/30 hover:shadow-sm transition-all group text-left"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cream rounded-lg flex items-center justify-center group-hover:bg-sage/10 transition-colors">
                <Upload className="w-5 h-5 text-navy" />
              </div>
              <span className="font-medium text-navy">Import CSV</span>
            </div>
            <ChevronRight className="w-4 h-4 text-warm-gray group-hover:text-sage transition-colors" />
          </div>
        </button>

        <button
          onClick={() => setShowExportModal(true)}
          className="bg-white rounded-xl p-4 border border-warm-gray/10 hover:border-sage/30 hover:shadow-sm transition-all group text-left"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cream rounded-lg flex items-center justify-center group-hover:bg-sage/10 transition-colors">
                <Download className="w-5 h-5 text-navy" />
              </div>
              <span className="font-medium text-navy">Export</span>
            </div>
            <ChevronRight className="w-4 h-4 text-warm-gray group-hover:text-sage transition-colors" />
          </div>
        </button>

        <Link
          to="/family/expenses/reports"
          className="bg-white rounded-xl p-4 border border-warm-gray/10 hover:border-sage/30 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cream rounded-lg flex items-center justify-center group-hover:bg-sage/10 transition-colors">
                <FileText className="w-5 h-5 text-navy" />
              </div>
              <span className="font-medium text-navy">Tax Report</span>
            </div>
            <ChevronRight className="w-4 h-4 text-warm-gray group-hover:text-sage transition-colors" />
          </div>
        </Link>
      </div>

      {/* Charts Row */}
      {categoryData && categoryData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 border border-warm-gray/10">
            <h3 className="font-semibold text-navy mb-4">Expenses by Category</h3>
            <ExpenseCategoryChart data={categoryData} />
          </div>
          <div className="bg-white rounded-xl p-6 border border-warm-gray/10">
            <h3 className="font-semibold text-navy mb-4">Monthly Trend</h3>
            <ExpenseTrendChart familyId={familyId} />
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-gray hover:text-navy"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
              title="Filters"
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              onClick={() => refetchExpenses()}
              className="p-2 text-warm-gray hover:bg-cream rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-warm-gray/20" />
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                viewMode === 'cards' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                viewMode === 'table' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
            >
              Table
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-warm-gray/10">
            <ExpenseFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClear={clearFilters}
            />
          </div>
        )}
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
        {isLoadingExpenses ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
          </div>
        ) : expensesError ? (
          <div className="text-center py-16">
            <p className="text-red-500">Error loading expenses</p>
            <button
              onClick={() => refetchExpenses()}
              className="mt-2 text-sage hover:underline"
            >
              Try again
            </button>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-16">
            <DollarSign className="w-12 h-12 text-warm-gray/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-navy mb-2">
              {searchQuery || Object.keys(filters).length > 0
                ? 'No expenses found'
                : 'No expenses yet'}
            </h3>
            <p className="text-warm-gray mb-4">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Try adjusting your search or filters'
                : 'Start tracking your care-related expenses'}
            </p>
            {!searchQuery && Object.keys(filters).length === 0 && (
              <Link
                to="/family/expenses/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Expense
              </Link>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {expenses.map((expense) => (
              <ExpenseCard
                key={expense.id}
                expense={expense}
                onClick={() => handleExpenseClick(expense)}
                onEdit={() => handleEditExpense(expense)}
                onDelete={() => handleDeleteExpense(expense)}
              />
            ))}
          </div>
        ) : (
          <ExpenseTable
            expenses={expenses}
            onRowClick={handleExpenseClick}
            onEdit={handleEditExpense}
            onDelete={handleDeleteExpense}
          />
        )}
      </div>

      {/* Modals */}
      {showExportModal && (
        <ExpenseExportModal
          familyId={familyId}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {showImportModal && (
        <ExpenseImportModal
          familyId={familyId}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            refetchExpenses();
          }}
        />
      )}
    </div>
  );
}
